import React, { useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import styles from './DatePicker.module.scss';
import classNames from 'classnames';
import collapseIcon from '../../../assets/collapse-black.svg';
import { firstPageIcon, lastPageIcon } from '../../../assets/hostedassets';
import Input from '../Input/Input';
import { formatDateString, DATE_FORMATS } from '../../../utils/dateUtils';

// dateUtils.ts's shared dayjs setup deliberately doesn't extend this plugin (see its
// parsePartialDate comment) - extended locally here, same as FetchGstModal.tsx/
// SectorIndustryOutlook.tsx, so strict two-arg parsing below actually validates the format.
dayjs.extend(customParseFormat);

type DateToken = 'DD' | 'MM' | 'YYYY';

// Token order + separator derived from `displayFormat` (e.g. 'DD-MM-YYYY' -> order
// ['DD','MM','YYYY'], separator '-') so typed digits mask into the same layout the picker
// already displays. Only numeric DD/MM/YYYY tokens are supported - the formats this component's
// callers pass as displayFormat when opting into manual typing.
const getManualInputTokens = (displayFormat: string): { order: DateToken[]; separator: string } => {
    const separatorMatch = displayFormat.match(/[^A-Za-z]/);
    const separator = separatorMatch ? separatorMatch[0] : '-';
    const order = displayFormat
        .split(separator)
        .filter((token): token is DateToken => token === 'DD' || token === 'MM' || token === 'YYYY');
    return { order, separator };
};

// Reformats raw keystrokes into the masked display format as the user types: strips
// non-digits, caps at 8 digits (dd+mm+yyyy), auto-inserts the separator between segments -
// the user never types the separator themselves.
const formatTypedDate = (raw: string, displayFormat: string): string => {
    const { order, separator } = getManualInputTokens(displayFormat);
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let cursor = 0;
    const segments = order.map((token) => {
        const length = token === 'YYYY' ? 4 : 2;
        const segment = digits.slice(cursor, cursor + length);
        cursor += length;
        return segment;
    });
    return segments.filter(Boolean).join(separator);
};

// Parses a fully-typed masked string back to a real Date via dayjs' strict customParseFormat
// mode, which rejects a partial (still-being-typed) string and a day/month rollover (e.g. 31/02)
// outright rather than silently normalising it. Returns undefined for anything invalid - the
// caller treats that exactly like an empty/invalid value.
const parseTypedDate = (typed: string, displayFormat: string): Date | undefined => {
    const parsed = dayjs(typed, displayFormat, true);
    return parsed.isValid() ? parsed.toDate() : undefined;
};

interface DatePickerProps {
    value?: string;
    onChange?: (date: string) => void;
    min?: Date | string;
    max?: Date | string;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    unit?: React.ReactNode;
    displayFormat?: string;
    responseFormat?: string;
    useFixedPosition?: boolean;
    calendarWidth?: number;
    hideInput?: boolean;
    isOpen?: boolean;
    showFloatingLabel?: boolean;
    /**
     * Opt-in: makes the text input typeable (free-text, masked to `displayFormat`) in addition
     * to the calendar picker. Defaults to false, which keeps every existing caller byte-identical
     * (readOnly, click-only) to today.
     */
    allowManualInput?: boolean;
    unitStyle?: React.CSSProperties;
    /** Inline styles applied directly to the `<input>` element. */
    inputStyle?: React.CSSProperties;
    /**
     * Inline styles for the div that wraps the entire input + unit together.
     * Controls the outer input container's size, border, layout, etc.
     */
    inputContainerStyle?: React.CSSProperties;
    /**
     * Inline styles for the div that wraps the unit element (sits outside the input).
     * Use when you need programmatic style changes on the unit wrapper (e.g. conditional background).
     */
    unitContainerStyle?: React.CSSProperties;
    /** Extra CSS class applied to the `<input>` element itself (e.g. for text colour or font changes). */
    inputClassName?: string;
    /**
     * Extra CSS class on the div that wraps the entire input + unit together.
     * Useful for overriding border radius, border colour, or background of the input container.
     */
    inputContainerClassName?: string;
    /**
     * Extra CSS class on the outer div wrapping the unit element.
     * Use this to change the unit wrapper's border, background, or shadow when the date filter
     * is in an "active / applied" state (e.g. highlight the calendar icon area in purple).
     */
    unitContainerClassName?: string;
    /**
     * Extra CSS class applied directly to the inner unit content element (the icon or text node).
     * Use this to change the icon's colour, size, or opacity (e.g. tint the calendar icon purple
     * when a custom filter is applied).
     */
    unitClassName?: string;
    /** Forwarded to the underlying `<input>`'s `aria-label` — for a picker with no visible `<label>`. */
    ariaLabel?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    min,
    max,
    disabled = false,
    className,
    placeholder = 'Select date',
    unit,
    displayFormat = DATE_FORMATS.DATE_FORMAT_DDMMYYYY,
    responseFormat = DATE_FORMATS.ISO,
    useFixedPosition = false,
    calendarWidth = 264,
    showFloatingLabel = false,
    unitStyle,
    inputStyle,
    inputContainerStyle,
    unitContainerStyle,
    inputClassName,
    inputContainerClassName,
    unitContainerClassName,
    unitClassName,
    hideInput = false,
    isOpen: isOpenProp = false,
    allowManualInput = false,
    ariaLabel,
}) => {
    const [currentDate, setCurrentDate] = useState(value ? new Date(value) : new Date());
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value ? new Date(value) : undefined);
    const [manualInputText, setManualInputText] = useState(() =>
        allowManualInput && value ? formatDateString(new Date(value), displayFormat) : '',
    );
    // Tracks the last `value` prop seen so an EXTERNAL change (calendar pick, parent reset) can
    // re-derive the typed text from it, without clobbering the exact keystrokes the user is
    // mid-typing - the same render-time prop-sync pattern used by rocket-loans-demo's DobField.
    const [prevManualSyncValue, setPrevManualSyncValue] = useState(value);
    if (allowManualInput && value !== prevManualSyncValue) {
        setPrevManualSyncValue(value);
        const typedDate = parseTypedDate(manualInputText, displayFormat);
        const typedValueMatchesProp = typedDate && formatDateString(typedDate, responseFormat) === value;
        if (!typedValueMatchesProp) {
            setManualInputText(value ? formatDateString(new Date(value), displayFormat) : '');
        }
    }
    const calendarRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);
    const [initialPositionCalculated, setInitialPositionCalculated] = useState(false);

    // Internal state for managing calendar open/close when isOpen prop is not provided
    const [isOpenInternal, setIsOpenInternal] = useState(false);

    // Use isOpenProp if provided, otherwise use internal state
    const isOpen = isOpenProp !== false ? isOpenProp : isOpenInternal;

    // Syncs calendar view and selection with the value prop.
    // Skips update while calendar is open to avoid disrupting user mid-navigation.
    React.useEffect(() => {
        if (isOpen) return;

        if (!value) {
            setSelectedDate(undefined);
            setCurrentDate(new Date());
            return;
        }

        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) {
            setSelectedDate(undefined);
            return;
        }

        setCurrentDate(parsed);
        setSelectedDate(parsed);
    }, [value, isOpen]);

    // Calculate initial position when calendar is about to open
    const calculateInitialPosition = () => {
        if (useFixedPosition) {
            const referenceElement = inputRef.current || containerRef.current;
            if (!referenceElement) return;

            const inputRect = referenceElement.getBoundingClientRect();
            const estimatedCalendarHeight = 400;
            const calendarW = calendarWidth;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = inputRect.bottom + 5;
            if (top + estimatedCalendarHeight > viewportHeight) {
                top = Math.max(10, viewportHeight - estimatedCalendarHeight - 10);
            }

            let left = inputRect.right - calendarW;
            if (left < 10) {
                left = 10;
            } else if (left + calendarW > viewportWidth) {
                left = Math.max(10, viewportWidth - calendarW - 10);
            }

            setCalendarPosition({ top, left });
            setInitialPositionCalculated(true);
        }
    };

    const updatePosition = () => {
        if (useFixedPosition && calendarRef.current) {
            const referenceElement = inputRef.current || containerRef.current;
            if (!referenceElement) return;

            const inputRect = referenceElement.getBoundingClientRect();
            const estimatedCalendarHeight = 400;
            const calendarW = calendarWidth;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = inputRect.bottom + 5;
            if (top + estimatedCalendarHeight > viewportHeight) {
                top = Math.max(10, viewportHeight - estimatedCalendarHeight - 10);
            }

            let left = inputRect.right - calendarW;
            if (left < 10) {
                left = 10;
            } else if (left + calendarW > viewportWidth) {
                left = Math.max(10, viewportWidth - calendarW - 10);
            }

            setCalendarPosition({ top, left });
            setIsPositioned(true);
        }
    };

    // Reset positioning when opening
    useEffect(() => {
        if (isOpen) {
            setIsPositioned(false);
            setInitialPositionCalculated(false);
            // Calculate position immediately when opening
            if (useFixedPosition) {
                calculateInitialPosition();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && useFixedPosition) {
            // Remove the setTimeout and calculate position immediately
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition);

            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition);
            };
        } else if (!isOpen) {
            setIsPositioned(false);
        }
    }, [isOpen, useFixedPosition]);

    // Update position after calendar is rendered
    useEffect(() => {
        if (isOpen && useFixedPosition && initialPositionCalculated) {
            // Use requestAnimationFrame to ensure the calendar is rendered
            requestAnimationFrame(() => {
                updatePosition();
            });
        }
    }, [isOpen, useFixedPosition, initialPositionCalculated]);

    useEffect(() => {
        if (isOpen && useFixedPosition) {
            updatePosition();
        }
    }, [currentDate, isOpen, useFixedPosition]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // If hideInput is true, let the parent component handle click-outside logic
            if (hideInput) {
                return;
            }

            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                ((inputRef.current && !inputRef.current.contains(event.target as Node)) ||
                    (hideInput && containerRef.current && !containerRef.current.contains(event.target as Node)))
            ) {
                // Close internal state if we're using it
                if (isOpenProp === false) {
                    setIsOpenInternal(false);
                }
                // Let parent component handle closing if using isOpen prop
                // The parent should detect this and set isOpen to false
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [isOpen, hideInput, isOpenProp]);

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const isDateDisabled = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let minDate: Date | null = null;
        let maxDate: Date | null = null;

        // Handle min date
        if (min) {
            if (min === '0') {
                minDate = today;
            } else if (!isNaN(Number(min))) {
                // Relative days from today
                minDate = new Date(today);
                minDate.setDate(today.getDate() + Number(min.toString()));
            } else if (!isNaN(Date.parse(min.toString()))) {
                // Absolute date
                minDate = new Date(min.toString());
            }
            if (minDate) minDate.setHours(0, 0, 0, 0);
        }

        // Handle max date
        if (max) {
            if (max === '0') {
                maxDate = today;
            } else if (!isNaN(Number(max))) {
                // Relative days from today
                maxDate = new Date(today);
                maxDate.setDate(today.getDate() + Number(max.toString()));
            } else if (!isNaN(Date.parse(max.toString()))) {
                // Absolute date
                maxDate = new Date(max.toString());
            }
            if (maxDate) maxDate.setHours(23, 59, 59, 999);
        }

        // Apply the constraints
        if (minDate && date < minDate) return true;
        if (maxDate && date > maxDate) return true;

        return false;
    };

    // Append-only by design: the controlled `value` is always the freshly-masked string, so the
    // caret lands at the end after every keystroke rather than staying where it was clicked. DOB
    // is a short left-to-right entry, so mid-string caret preservation was judged disproportionate
    // (and mask-index bug-prone) for what it buys here.
    const handleManualInputChange = (raw: string) => {
        const formatted = formatTypedDate(raw, displayFormat);
        setManualInputText(formatted);

        // Deleting an existing value down to empty must clear the parent's value too - otherwise
        // the parent keeps the stale date even though the input now shows nothing.
        if (!formatted) {
            setSelectedDate(undefined);
            onChange?.('');
            return;
        }

        const parsedDate = parseTypedDate(formatted, displayFormat);
        if (!parsedDate || isDateDisabled(parsedDate)) return;

        setSelectedDate(parsedDate);
        setCurrentDate(parsedDate);
        onChange?.(formatDateString(parsedDate, responseFormat));
    };

    const handleDateSelect = (day: number) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        if (isDateDisabled(newDate)) return;

        setSelectedDate(newDate);
        onChange?.(formatDateString(newDate, responseFormat));
        setIsPositioned(false);

        // Close internal state if we're using it
        if (isOpenProp === false) {
            setIsOpenInternal(false);
        }
    };

    const handleOtherMonthDateSelect = (day: number, monthOffset: number) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, day);
        if (isDateDisabled(newDate)) return;

        changeMonth(monthOffset);
        setSelectedDate(newDate);
        onChange?.(formatDateString(newDate, responseFormat));
        setIsPositioned(false);

        // Close internal state if we're using it
        if (isOpenProp === false) {
            setIsOpenInternal(false);
        }
    };

    const changeMonth = (offset: number) => {
        setCurrentDate((prev) => {
            const newDate = new Date(prev.getFullYear(), prev.getMonth() + offset, 1);
            return newDate;
        });
    };

    const changeYear = (offset: number) => {
        setCurrentDate((prev) => {
            const newDate = new Date(prev.getFullYear() + offset, prev.getMonth(), 1);
            return newDate;
        });
    };

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days: JSX.Element[] = [];

        // Add days from previous month
        const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const daysInPrevMonth = getDaysInMonth(prevMonth);
        for (let i = 0; i < firstDay; i++) {
            const day = daysInPrevMonth - firstDay + i + 1;
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, day);
            const isDisabled = isDateDisabled(date);

            days.push(
                <button
                    key={`prev-${day}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOtherMonthDateSelect(day, -1);
                    }}
                    disabled={isDisabled}
                    className={classNames(styles.day, styles.otherMonth, {
                        [styles.disabled]: isDisabled,
                    })}
                >
                    {day}
                </button>,
            );
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const isDisabled = isDateDisabled(date);
            const isSelected =
                selectedDate?.getDate() === day &&
                selectedDate?.getMonth() === currentDate.getMonth() &&
                selectedDate?.getFullYear() === currentDate.getFullYear();

            days.push(
                <button
                    key={day}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDateSelect(day);
                    }}
                    disabled={isDisabled}
                    className={classNames(styles.day, {
                        [styles.disabled]: isDisabled,
                        [styles.selected]: isSelected,
                    })}
                >
                    {day}
                </button>,
            );
        }

        // Calculate if we need 6 rows or 5 rows
        const totalDays = firstDay + daysInMonth;
        const numberOfRows = Math.ceil(totalDays / 7);
        const totalCells = numberOfRows * 7;

        // Add days from next month only up to the required cells
        const remainingDays = totalCells - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
            const isDisabled = isDateDisabled(date);

            days.push(
                <button
                    key={`next-${day}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOtherMonthDateSelect(day, 1);
                    }}
                    disabled={isDisabled}
                    className={classNames(styles.day, styles.otherMonth, {
                        [styles.disabled]: isDisabled,
                    })}
                >
                    {day}
                </button>,
            );
        }

        return days;
    };

    return (
        <div className={classNames(styles.datePicker, className)} ref={containerRef}>
            {!hideInput && (
                <div
                    ref={inputRef}
                    onClick={() => {
                        // Only toggle if we're using internal state (isOpenProp is false)
                        if (isOpenProp === false) {
                            setIsOpenInternal(!isOpenInternal);
                        }
                    }}
                >
                    <Input
                        type="text"
                        ariaLabel={ariaLabel}
                        style={{ margin: 0, height: '44px', ...inputContainerStyle }}
                        containerClassName={inputContainerClassName}
                        value={allowManualInput ? manualInputText : value ? formatDateString(new Date(value), displayFormat) : ''}
                        readOnly={!allowManualInput}
                        disabled={disabled}
                        placeholder={placeholder}
                        inputStyle={{ cursor: allowManualInput ? 'text' : 'pointer', width: '100%', ...inputStyle }}
                        inputClassName={inputClassName}
                        onChange={allowManualInput ? handleManualInputChange : () => {}}
                        unit={unit}
                        unitStyle={{ padding: '0.2rem 0 0.2rem 4rem', ...unitStyle }}
                        unitContainer={unitContainerStyle}
                        unitContainerClassName={unitContainerClassName}
                        unitClassName={unitClassName}
                        isActive={isOpen}
                        showFloatingLabel={showFloatingLabel}
                    />
                </div>
            )}

            {isOpen && (!useFixedPosition || initialPositionCalculated) && (
                <div
                    ref={calendarRef}
                    className={styles.calendar}
                    onClick={(e) => e.stopPropagation()}
                    style={
                        useFixedPosition
                            ? {
                                  position: 'fixed',
                                  top: `${calendarPosition.top}px`,
                                  left: `${calendarPosition.left}px`,
                                  width: `${calendarWidth}px`,
                                  zIndex: 999999,
                              }
                            : {}
                    }
                >
                    <div className={styles.header}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                changeYear(-1);
                            }}
                            className={styles.navButton}
                            aria-label="Previous year"
                        >
                            <img src={firstPageIcon} alt="Previous year" className={styles.doubleArrowIcon} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                changeMonth(-1);
                            }}
                            className={styles.navButton}
                        >
                            <img src={collapseIcon} alt="Previous month" className={styles.prevMonth} />
                        </button>
                        <span>{formatDateString(currentDate, DATE_FORMATS.MONTH_FULL_YEAR)}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                changeMonth(1);
                            }}
                            className={styles.navButton}
                        >
                            <img src={collapseIcon} alt="Next month" className={styles.nextMonth} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                changeYear(1);
                            }}
                            className={styles.navButton}
                            aria-label="Next year"
                        >
                            <img src={lastPageIcon} alt="Next year" className={styles.doubleArrowIcon} />
                        </button>
                    </div>

                    <div className={styles.weekdays}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <div key={day} className={styles.weekday}>
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className={styles.days}>{renderCalendar()}</div>
                </div>
            )}
        </div>
    );
};
